import { observable, action, runInAction, configure, computed, makeObservable } from "mobx";
import * as Request from "superagent";
import ServicePlanModel from "./models/ServicePlanModel";
import { VehicleModelData, CmsTab } from "./models/ServicePlanModels";
import servicePlanCMSModel from "./models/servicePlanCMSModel";
import VariantCappedServiceTotalModel from "./models/VariantCappedServiceTotal";
import CappedPricingCMSModel from "../CappedPriceService/models/CappedPricingCMSModel";
import CappedPricingServiceModel from "../CappedPriceService/models/CappedPricingServiceModel";
import { ApiBasePath } from "../../utils/ApiBasePath";

const serviceDetailApiUrl = `${ApiBasePath.serviceDetails}/vehicle-model/`; //"/api/servicedetails/vehicle-model/";
configure({ enforceActions: "never" });

export default class ServicePlanStore {
  @observable servicePlan?: ServicePlanModel = new ServicePlanModel();
  @observable servicePlanCMSModel?: servicePlanCMSModel =
    new servicePlanCMSModel();
  @observable variantCappedServiceTotalModel?: VariantCappedServiceTotalModel =
    new VariantCappedServiceTotalModel();
  @observable cappedPricingCMSModel?: CappedPricingCMSModel =
    new CappedPricingCMSModel();
  @observable cappedPricing?: CappedPricingServiceModel =
    new CappedPricingServiceModel();
  @observable servicePlanSelectedModel?: string;
  @observable cappedPricingSelectedModel?: string;
  @observable modelVariantsSelected?: string;

  // ─── New BlockList-driven tab state ─────────────────────────────────────────
  /** All tabs from the CMS BlockList. Null when the CMS property hasn't been configured yet. */
  @observable cpsTabs: CmsTab[] | null = null;
  @observable selectedTabId: string | null = null;
  @observable cappedPricingCpsTabs: CmsTab[] | null = null;
  @observable selectedCappedPricingTabId: string | null = null;
  @observable showHiddenTable: boolean = false;

  constructor() {
    makeObservable(this);
  }

  @action setServicePlanSelectedModel = (value) => {
    this.servicePlanSelectedModel = value;
  };

  @action setSelectedTabId = (tabId: string) => {
    this.selectedTabId = tabId;
    this.showHiddenTable = false;
  };

  @action setSelectedCappedPricingTabId = (tabId: string) => {
    this.selectedCappedPricingTabId = tabId;
    this.showHiddenTable = false;
  };

  @action setShowHiddenTable = (value: boolean) => {
    this.showHiddenTable = value;
  };

  @action setCappedPricingSelectedModel(newName: string) {
    this.cappedPricingSelectedModel = newName;
  }

  @action setModelVariantSelected(model: string) {
    this.modelVariantsSelected = model;
  }

  @action resetDetails = () => {
    this.servicePlan.reset();
    this.cappedPricing.reset();
    this.variantCappedServiceTotalModel.reset();
  };

  @computed get vehicleModelsListForSelectControlServicePlan() {
    return this.servicePlanCMSModel.response.map((x) => ({
      value: x.modelID,
      label: x.name,
    }));
  }
  @computed get vehicleModelsListForSelectControlCPS() {
    return this.cappedPricingCMSModel.response.map((x) => ({
      value: x.modelID,
      label: x.name,
    }));
  }
  
  // ─── Computed selectors for the active tab ────────────────────────────────
  @computed get selectedServicePlanTab(): CmsTab | null {
    if (!this.cpsTabs || !this.selectedTabId) return null;
    return this.cpsTabs.find((t) => t.tabId === this.selectedTabId) ?? null;
  }

  @computed get selectedCappedPricingTab(): CmsTab | null {
    if (!this.cappedPricingCpsTabs || !this.selectedCappedPricingTabId) return null;
    return this.cappedPricingCpsTabs.find((t) => t.tabId === this.selectedCappedPricingTabId) ?? null;
  }

  /**
   * Returns all variant GUIDs across all columns of the active tab.
   * Used to filter API interval data dynamically — replaces hardcoded Wilderness/XT string checks.
   */
  @computed get activeTabVariantGuids(): Set<string> {
    const tab = this.selectedServicePlanTab;
    if (!tab) return new Set();
    return new Set(
      tab.columns.flatMap((col) => col.variants.map((v) => v.variantGuid.toUpperCase()))
    );
  }

  @computed get activeCappedPricingTabVariantGuids(): Set<string> {
    const tab = this.selectedCappedPricingTab;
    if (!tab) return new Set();
    return new Set(
      tab.columns.flatMap((col) => col.variants.map((v) => v.variantGuid.toUpperCase()))
    );
  }

  // ─── Legacy computed getters (unchanged) ─────────────────────────────────
  @computed get servicePlanResponse() {
    return this.servicePlan?.response ?? [];
  }
  
  @computed get servicePlanIsLoading() {
    return this.servicePlan?.isLoading ?? true;
  }
  
  @computed get servicePlanIsContentExist() {
    return this.servicePlan?.isContentExist ?? false;
  }
  
  @computed get servicePlanIsRequestError() {
    return this.servicePlan?.isRequestError ?? false;
  }
  //Fecthing Umbraco CMS data
  @action fetchServicePlanContentData = () => {
    // Legacy models are removed. Load CMS tabs directly.
    const tabs: CmsTab[] | null = window["serviceDetailsRoot"].cpsTabs ?? null;
    this.servicePlanCMSModel.response = [];
    if (tabs && tabs.length > 0) {
      this.cpsTabs = tabs;
      this.selectedTabId = tabs[0].tabId;
      if (!this.servicePlanSelectedModel && tabs[0].modelId) {
        this.servicePlanSelectedModel = tabs[0].modelId;
      }
    }
  };

  //Fecthing Umbraco CMS data
  @action fetchCappedPricingContentData = () => {
    // Legacy models are removed. Load CMS tabs directly.
    const tabs: CmsTab[] | null = window["serviceDetailsRoot"].cpsTabs ?? null;
    this.cappedPricingCMSModel.response = [];
    if (tabs && tabs.length > 0) {
      this.cappedPricingCpsTabs = tabs;
      this.selectedCappedPricingTabId = tabs[0].tabId;
      if (!this.cappedPricingSelectedModel && tabs[0].modelId) {
        this.cappedPricingSelectedModel = tabs[0].modelId;
      }
    }
  };

  @action CalculateServicePlanTotalPrice = (model) => {
    //check if total already exists
    if (
      this.variantCappedServiceTotalModel.response.findIndex(
        (m) =>
          m.modelID.toLocaleLowerCase() === model.modelID.toLocaleLowerCase()
      ) === -1
    ) {
      const selectedApiModel = this.servicePlan.response.find(
        (m) => m.id.toLocaleLowerCase() === model.modelID.toLocaleLowerCase()
      );

      if (!selectedApiModel) return;

      const selectedDesktopModelRaw = this.servicePlanCMSModel.response.find(
        (m) => m.modelID.toLocaleLowerCase() === model.modelID.toLocaleLowerCase()
      );

      const selectedDesktopModel = selectedDesktopModelRaw
        ? selectedDesktopModelRaw.variants.filter((variant) => !!variant.variantGUID || !!variant.pimIdentifier)
        : (selectedApiModel.intervalData[0]?.modelVariantServicePrices || []).map((v: any) => ({
            variantGUID: v.variantGUID,
            pimIdentifier: v.pimIdentifier,
            servicePriceVariantName: v.variantName,
          }));

      let threeYearElementIndex = selectedApiModel.intervalData.findIndex(
        (item) => item.name.includes("36 months")
      );

      let i: number;

      selectedDesktopModel.forEach((element) => {
        let intervalData = null;

        if (model.name.toLowerCase().includes("outback")) {
          if (element.servicePriceVariantName.includes("Wilderness")) {
            intervalData = selectedApiModel.intervalData.filter(
              (i) =>
                i.name.includes("Health Check and Chat") ||
                i.modelVariantServicePrices.find(
                  (m) =>
                    !m.isNotApplicable && m.variantName.includes("Wilderness")
                )
            );
          } else if (element.servicePriceVariantName.includes("XT")) {
            intervalData = selectedApiModel.intervalData.filter(
              (i) =>
                i.name.includes("Health Check and Chat") ||
                i.modelVariantServicePrices.find(
                  (m) => !m.isNotApplicable && m.variantName.includes("XT")
                )
            );
          } else {
            intervalData = selectedApiModel.intervalData.filter(
              (i) =>
                i.name.includes("Health Check and Chat") ||
                i.modelVariantServicePrices.find(
                  (m) =>
                    !m.isNotApplicable &&
                    !(
                      m.variantName.includes("XT") ||
                      m.variantName.includes("Wilderness")
                    )
                )
            );
          }
        } else {
          intervalData = selectedApiModel.intervalData;
        }

        threeYearElementIndex = intervalData.findIndex((item) =>
          item.name.includes("36 months")
        );

        for (i = 0; i < intervalData.length; i++) {
          const cappedVariant = intervalData[i].modelVariantServicePrices.find(
            (variant) => {
              const apiGuid = (variant.variantGUID as string).toUpperCase();
              const apiPim = variant.pimIdentifier ? (variant.pimIdentifier as string).toUpperCase() : null;
              const matchGuid = element.variantGUID ? element.variantGUID.toUpperCase() : null;
              const matchPim = element.pimIdentifier ? element.pimIdentifier.toUpperCase() : null;
              
              return (matchGuid && apiGuid === matchGuid) || 
                     (matchPim && apiPim === matchPim) ||
                     (matchPim && apiGuid === matchPim) ||
                     (matchGuid && apiPim === matchGuid);
            }
          );

          let cappedVariantPrice = cappedVariant ? cappedVariant.price : 0;

          let variantIndex =
            this.variantCappedServiceTotalModel.response.findIndex(
              (variantTotal) => {
                const totalGuid = (variantTotal.variantGUID as string).toUpperCase();
                const totalPim = variantTotal.pimIdentifier ? (variantTotal.pimIdentifier as string).toUpperCase() : null;
                const matchGuid = element.variantGUID ? element.variantGUID.toUpperCase() : null;
                const matchPim = element.pimIdentifier ? element.pimIdentifier.toUpperCase() : null;
                
                return (matchGuid && totalGuid === matchGuid) || 
                       (matchPim && totalPim === matchPim) ||
                       (matchPim && totalGuid === matchPim) ||
                       (matchGuid && totalPim === matchGuid);
              }
            );

          if (variantIndex > -1) {
            let currentVariant =
              this.variantCappedServiceTotalModel.response[variantIndex];

            if (i <= threeYearElementIndex) {
              currentVariant.threeYearsPrice =
                cappedVariantPrice + currentVariant.threeYearsPrice;
            }
            currentVariant.totalPrice =
              cappedVariantPrice + currentVariant.totalPrice;
          } else {
            if (cappedVariant) {
              this.variantCappedServiceTotalModel.response.push({
                threeYearsPrice: cappedVariantPrice,
                totalPrice: cappedVariantPrice,
                variantGUID: element.variantGUID,
                pimIdentifier: element.pimIdentifier,
                modelName: model.name,
                modelID: model.modelID,
              });
            }
          }
        }
      });
    }
  };

  //Fetching vehicle api's Service Plan data
  @action fetchServicePlanData = () => {
    this.servicePlan.isLoading = true;
    let url = serviceDetailApiUrl + "service-plans";

    Request.get(url).then(
      (response) => {
        runInAction(() => {
          try {
            const vehicleModelData = response.body;

            this.servicePlan.response = this.mapToVehicleModelDataArray(
              "service-plans",
              vehicleModelData
            );

            this.servicePlan.isContentExist = true;
            this.servicePlanCMSModel.response = [];

            const hasLegacyModels = this.servicePlanCMSModel.response.length > 0;
            const hasTabs = (this.cpsTabs?.length ?? 0) > 0;

            if (
              this.variantCappedServiceTotalModel.response.length === 0 &&
              (hasLegacyModels || hasTabs) &&
              this.servicePlan.response.length > 0
            ) {
              let firstModel;
              if (hasLegacyModels) {
                firstModel = this.servicePlanCMSModel.response[0];
              } else if (hasTabs && this.cpsTabs && this.cpsTabs.length > 0) {
                firstModel = {
                  modelID: this.cpsTabs[0].modelId,
                  name: this.cpsTabs[0].tabName || ""
                };
              } else {
                firstModel = {
                  modelID: this.servicePlan.response[0].id,
                  name: this.servicePlan.response[0].name,
                };
              }
              this.CalculateServicePlanTotalPrice(firstModel);
            }
          } catch (e) {
            console.error("Error processing service plan data", e);
            this.servicePlan.isRequestError = true;
          } finally {
            this.servicePlan.isLoading = false;
          }
        });
      },
      (error) => {
        console.error("Service Plan API Error:", error);
        runInAction(() => {
          this.servicePlan.reset();
          this.servicePlan.isContentExist = false;
          this.servicePlan.isLoading = false;
          this.servicePlan.isRequestError = true;
        });
      }
    );
  };
  //Fetching vehicle api's capped services data
  @action fetchCappedServices = () => {
    let url = serviceDetailApiUrl + "capped-services";
    Request.get(url).then(
      (response) => {
        runInAction(() => {
          const vehicleModelData = response.body;
          this.cappedPricing.response = this.mapToVehicleModelDataArray(
            "capped-services",
            vehicleModelData
          );
          this.cappedPricing.isServiceIntervalExist = true;
          this.cappedPricing.isRequestError = false;
          this.cappedPricingCMSModel.response = [];
        });
      },
      (error) => {
        runInAction(() => {
          this.cappedPricing.reset();
          this.cappedPricing.isServiceIntervalExist = false;
          this.cappedPricing.isRequestError = true;
        });
      }
    );
  };

  mapToVehicleModelDataArray = (serviceType, vehicleModelData) => {
    // In tab mode the CMS cpsModels may be empty (legacy "Models" picker removed).
    // The tab columns carry their own variant GUIDs + display names, so we do not
    // need the CMS lookup to filter or name variants — include all API variants.
    const isTabMode =
      serviceType === "service-plans"
        ? (this.cpsTabs?.length ?? 0) > 0
        : (this.cappedPricingCpsTabs?.length ?? 0) > 0;

    let vehicleModels: VehicleModelData[] = [];
    vehicleModels = vehicleModelData
      .filter(function (model) {
        return model.isActive;
      })
      .sort((a, b) => this.sortFn(a.displayOrder, b.displayOrder))
      .map((model) => ({
        id: model.id,
        name: model.name,
        intervalData: this.getServicesList(
          serviceType,
          model.vehicleVariants
        ).map((serviceDescription) => ({
          name: serviceDescription,
          modelVariantServicePrices: model.vehicleVariants
            .sort((a, b) => this.sortFn(a.displayOrder, b.displayOrder))
            .filter(
              (variant) =>
                isTabMode ||
                this.getServicePriceVariantName(
                  serviceType,
                  model.id,
                  variant.id
                ) !== null
            )
            .map((variant) => ({
              variantGUID: (variant.id as string).toUpperCase(),
              pimIdentifier: variant.pimIdentifier ? (variant.pimIdentifier as string).toUpperCase() : undefined,
              variantName: isTabMode
                ? (variant.name ?? "")
                : this.getServicePriceVariantName(
                    serviceType,
                    model.id,
                    variant.id
                  ),
              displayOrder: variant.displayOrder,
              price: this.getPrice(serviceType, variant, serviceDescription),
              isNotApplicable: this.getIsNotApplicable(
                serviceType,
                variant,
                serviceDescription
              ),
            })),
        })),
      }));
    return vehicleModels;
  };
  getIsNotApplicable = (serviceType, variant, serviceDescription) => {
    if (serviceType === "capped-services") {
      return !variant.cappedServices.cappedServices.find(
        (cappedItem) => cappedItem.description === serviceDescription
      );
    } else {
      return !variant.servicePlans.servicePlans.find(
        (servicePlanItem) => servicePlanItem.description === serviceDescription
      );
    }
  };
  getPrice = (serviceType, variant, serviceDescription) => {
    if (serviceType === "capped-services") {
      return !!variant.cappedServices.cappedServices.find(
        (cappedItem) => cappedItem.description === serviceDescription
      )
        ? variant.cappedServices.cappedServices.find(
            (cappedItem) => cappedItem.description === serviceDescription
          ).price
        : 0;
    } else {
      return !!variant.servicePlans.servicePlans.find(
        (servicePlanItem) => servicePlanItem.description === serviceDescription
      )
        ? variant.servicePlans.servicePlans.find(
            (servicePlanItem) =>
              servicePlanItem.description === serviceDescription
          ).price
        : 0;
    }
  };
  getServicesList = (serviceType, vehicleVariants) => {
    let flattened = [];
    if (serviceType === "capped-services") {
      vehicleVariants
        .sort((a, b) =>
          !!a.cappedServices &&
          !!b.cappedServices &&
          b.cappedServices.cappedServices.length >
            a.cappedServices.cappedServices.length
            ? 1
            : -1
        )
        .forEach((variant) => {
          if (!!variant.cappedServices) {
            variant.cappedServices.cappedServices
              .sort((a, b) => this.sortFn(a.displayOrder, b.displayOrder))
              .forEach((service) => flattened.push(service.description));
          }
        });
    } else {
      vehicleVariants
        .sort((a, b) =>
          !!a.servicePlans &&
          !!b.servicePlans &&
          b.servicePlans.servicePlans.length >
            a.servicePlans.servicePlans.length
            ? 1
            : -1
        )
        .forEach((variant) => {
          if (!!variant.servicePlans) {
            variant.servicePlans.servicePlans
              .sort((a, b) => this.sortFn(a.displayOrder, b.displayOrder))
              .forEach((service) => flattened.push(service.description));
          }
        });
    }

    const uniqueArray = Array.from(new Set(flattened));
    return uniqueArray;
  };

  sortFn = (a, b) => a - b;

  getServicePriceVariantName = (serviceType, modelID, vairantGUID) => {
    //find model
    let modelData = null;
    if (serviceType === "service-plans") {
      modelData = this.servicePlanCMSModel.response.find(
        (m) => !!m.modelID && m.modelID.toUpperCase() === modelID.toUpperCase()
      );
    } else if (serviceType === "capped-services") {
      modelData = this.cappedPricingCMSModel.response.find(
        (m) => !!m.modelID && m.modelID.toUpperCase() === modelID.toUpperCase()
      );
    }
    //if the model name cannot found, return null
    if (!modelData) return null;

    const variant = modelData.variants.find(
      (v) =>
        !!v.variantGUID &&
        v.variantGUID.toUpperCase() === vairantGUID.toUpperCase()
    );
    return !variant ? null : variant.servicePriceVariantName;
  };
}
